import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Menu,
  MenuItem,
  Checkbox,
} from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import { CATEGORY_VIEW, CATEGORY_EDIT, CATEGORY_CREATE } from '../../../../../constants/SpaConst';
import { useSelector, useDispatch } from 'react-redux';
import {
  allSpaInfoSelector,
  allSpaTreatmentsSelector,
  allSpaCategoriesSelector,
  fetchAllSpa,
  setAllSpaCategories,
} from '../../../../property/propertySlice';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { createDataSortMapping, deleteSpaCategoryAPI } from '../../../../../utils/PropertyAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTEL_SPA,
} from '../../../../../constants/RoleConsts';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import { DragIndicator, MoreHoriz } from '@material-ui/icons';
import MultipleDeleteConfirmationPopup from '../../../../../shared/MultiActionPopUp/MultiDeleteConfirmationPopup';
import { deleteSelectedItems } from '../../../../../utils/ServicesAPIUtils';
import { isDragDisabled } from '../../../../../utils/formUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';

const CategoryList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;
  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    spaCategory: null,
  });
  const originalSpaInfo = useSelector(allSpaInfoSelector);
  const originalSpaCategory = useSelector(allSpaCategoriesSelector);
  const originalSpaTreatments = useSelector(allSpaTreatmentsSelector);
  const [spaCategory, setSpaCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popUp, setPopUp] = useState(false);
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef(null);
  const prevHotelIdRef = useRef(selectedHotel);
  const [searchText, setSearchText] = useState('');
  const [showMultipleDeleteConfirmation, setShowMultipleDeleteConfirmation] = useState(false);
  const [actionName, setActionName] = useState(false);

  const [actionMenuList, setActionMenuList] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const checkedSpaCategory = spaCategory?.filter((sCategory) => sCategory?.isChecked);

  useEffect(() => {
    setSearchText('');
    setSpaCategory(
      originalSpaCategory?.map((spaCategory) => {
        return {
          ...spaCategory,
          isChecked: false,
        };
      }),
    );

    setLoading(false);
  }, [originalSpaInfo, originalSpaTreatments, originalSpaCategory]);

  useEffect(() => {
    if (loading || spaCategory?.length === 0) return;
    const totalItems = spaCategory?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [spaCategory?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const handleSearchText = (searchText) => {
    setSearchText(searchText);
    const filteredRows = originalSpaCategory?.filter((item) => {
      return item.name.toLowerCase().includes(searchText.toLowerCase());
    });
    onsetPage(0);
    setSpaCategory(filteredRows);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (event, type) => {
    event.stopPropagation();
    onDrawerToggle(type, true, selectedValue.spaCategory);
    handleClose();
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      spaCategory: null,
    });
  };

  const dragDisabled = isDragDisabled(spaCategory, searchText);

  const handleCellMenuClick = (event, spaCategoryItem) => {
    event.stopPropagation();
    setSelectedValue({
      id: spaCategoryItem.id,
      name: spaCategoryItem.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      spaCategory: spaCategoryItem,
    });
  };

  const deleteSpaCategory = async (e) => {
    e.preventDefault();
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      description:
        'This action cannot be reversed. Information associated with this category will also be lost',
      status: true,
      anchorEl: null,
    }));
  };

  const deleteSpaCategoryAPICall = async () => {
    try {
      const lang = decryptLocalStorage('lang');
      const delByLang = lang !== 'en' ? lang : '';
      if (selectedHotel && selectedValue && selectedValue.id) {
        await deleteSpaCategoryAPI(selectedValue.id, selectedHotel, delByLang);
        dispatch(fetchAllSpa(selectedHotel));
        notificationManager('success', 'Category deleted successfully');
      }
      handleClose();
    } catch (e) {
      if (e.graphQLErrors) {
        notificationManager('danger', e.graphQLErrors[0].message);
      } else {
        notificationManager('danger', 'Failed to delete Category');
      }
      handleClose();
    }
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      spaCategory: null,
    });
  };

  const onViewDetails = (categoryItem, e) => {
    if (e?.target?.tagName === 'TD') {
      onViewToggle(CATEGORY_VIEW, true, categoryItem);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.spaCategory) {
      onViewToggle(CATEGORY_VIEW, true, selectedValue.spaCategory);
    }
  };

  const retriveNoOfTreatments = (spaCategoryItem) => {
    return originalSpaTreatments.filter(
      (treatments) => treatments.spaCategoryId === spaCategoryItem.id,
    ).length;
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  const handleAction = (actionName) => {
    let selectedValue = spaCategory?.filter((SInfo) => SInfo?.isChecked);
    switch (actionName) {
      case 'Delete':
        if (selectedValue?.length > 0) {
          setShowMultipleDeleteConfirmation(true);
          setActionName('delete');
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteSpaCategoryAPICall}
          onDeleteCancel={onDeleteCancel}
        />
      )}

      <SearchBar
        type={CATEGORY_CREATE}
        buttonName='Add Category'
        displayAddOrEdit={
          permissions[RBAC_TYPE_HOTEL_SPA] === RBAC_PERMISSION_LEVEL_FULL_ACCESS ? 'show' : 'hide'
        }
        onOpenDrawer={onOpenDrawer}
        setSearchText={handleSearchText}
        visible={visible}
        setVisible={setVisible}
        popUp={popUp}
        setPopUp={setPopUp}
        placeHolder={spaCategory}
        setPlaceHolder={setSpaCategory}
        globalPlaceHolder={originalSpaCategory}
        setGlobalPlaceHolder={setAllSpaCategories}
        dispatch={dispatch}
        mappingApi={createDataSortMapping}
        displayText='Spa category'
        module='SPA_CATEGORY'
        searchText={searchText}
        setSearch={setSearchText}
        actionMenuItems={actionMenuList ?? []}
        handleAction={handleAction ?? false}
      />

      <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
        {loading ? (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        ) : (
          spaCategory && (
            <TableContainer>
              <Table className='list-table' aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell className='w-40'>Name</TableCell>
                    <TableCell className='w-20' align='right'>
                      No of Treatments
                    </TableCell>
                    <TableCell className='w-30'></TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>

                <DragDropContext
                  onDragEnd={(event) =>
                    handleOnDragEnd(
                      event,
                      setVisible,
                      spaCategory,
                      setSpaCategory,
                      false,
                      page,
                      rowsPerPage,
                    )
                  }
                  isDropDisabled={dragDisabled}
                >
                  <Droppable droppableId='spas' isDropDisabled={dragDisabled}>
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {(rowsPerPage > 0
                          ? spaCategory.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : spaCategory
                        ).map((spaCategoryItem, i) => (
                          <Draggable
                            key={spaCategoryItem.id}
                            draggableId={spaCategoryItem.id}
                            index={i}
                            isDragDisabled={dragDisabled}
                          >
                            {(provided) => (
                              <TableRow
                                key={i}
                                className='border-white pointer'
                                onClick={(e) => {
                                  onViewDetails(spaCategoryItem, e);
                                }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <TableCell>
                                  {!dragDisabled && (
                                    <DragIndicator className='res-menu-accordion-drag ' />
                                  )}
                                </TableCell>
                                <TableCell className='w-40'>{spaCategoryItem?.name}</TableCell>
                                <TableCell className='w-20' align='right'>
                                  {retriveNoOfTreatments(spaCategoryItem)}
                                </TableCell>
                                <TableCell className='w-30'></TableCell>
                                <TableCell className='kebab-menu' align='right'>
                                  {permissions[RBAC_TYPE_HOTEL_SPA] ===
                                    RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                    <Button
                                      aria-controls='simple-menu'
                                      aria-haspopup='true'
                                      onClick={(e) => {
                                        handleCellMenuClick(e, spaCategoryItem);
                                      }}
                                      className='showpopupInner'
                                    >
                                      <MoreHoriz className='showpopupInner' />
                                    </Button>
                                  )}
                                  <Menu
                                    id={i}
                                    anchorEl={selectedValue?.anchorEl}
                                    disableRestoreFocus
                                    MenuListProps={{ autoFocusItem: false }}
                                    keepMounted
                                    open={
                                      Boolean(selectedValue.anchorEl) &&
                                      selectedValue.id === spaCategoryItem.id
                                    }
                                    onClose={handleClose}
                                    className='shadow'
                                  >
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onOpenDrawer(e, CATEGORY_EDIT);
                                      }}
                                    >
                                      <CreateIcon className='me-2 action-menu-icon' />
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        onClickView(e);
                                      }}
                                    >
                                      <VisibilityIcon className='me-2 action-menu-icon' />
                                      View
                                    </MenuItem>
                                    <MenuItem
                                      className='pe-4'
                                      onClick={(e) => {
                                        deleteSpaCategory(e);
                                      }}
                                    >
                                      <DeleteForeverIcon className='me-2 action-menu-icon' />
                                      Delete
                                    </MenuItem>
                                  </Menu>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </DragDropContext>
                <TableFooter>
                  <TableRow className='paginator'>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      count={spaCategory.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { 'aria-label': 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )
        )}
      </div>
    </div>
  );
};

export default CategoryList;
